import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { contracts } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { Money } from "@/components/shared/Money";
import type { ContractStatus } from "@/types/api";

export function ContractList() {
  const [status, setStatus] = useState<ContractStatus | "">("");

  const q = useQuery({
    queryKey: ["contracts", status],
    queryFn: () => contracts.list({ status: status || undefined }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Contracts</h1>
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ContractStatus | "")}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm dark:bg-slate-800 dark:border-slate-700"
          >
            <option value="">all statuses</option>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="expired">expired</option>
            <option value="cancelled">cancelled</option>
          </select>
          <Link
            to="/contracts/new"
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-700"
          >
            New contract
          </Link>
        </div>
      </div>

      {q.isLoading ? (
        <Spinner />
      ) : q.error ? (
        <div className="text-red-700">{(q.error as Error).message}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Term</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Frequency</th>
                <th className="px-3 py-2 text-right">Devices</th>
              </tr>
            </thead>
            <tbody>
              {(q.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                    No contracts.
                  </td>
                </tr>
              )}
              {(q.data ?? []).map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/30"
                >
                  <td className="px-3 py-2">
                    <Link to={`/contracts/${c.id}`} className="font-medium hover:underline">
                      {c.customer_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{c.status}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {c.start_date} → {c.end_date}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={c.billing_amount} />
                  </td>
                  <td className="px-3 py-2">{c.billing_frequency}</td>
                  <td className="px-3 py-2 text-right">{c.netbox_device_ids.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
